// English texts - every key of de.ts, nothing else (the type makes a missing or extra key a compile error).

import type { TextKey } from "./de";

export const en: Readonly<Record<TextKey, string>> = {
  "app.name": "FitCalendar",
  "app.tagline": "Your rides in a calendar",
  "app.privacy": "Your files never leave the browser.",

  "action.import": "Import FIT",
  "action.importShort": "Import",
  "action.today": "Today",
  "action.prevMonth": "Previous month",
  "action.nextMonth": "Next month",
  "action.prevRide": "Previous ride",
  "action.nextRide": "Next ride",
  "action.back": "Back",
  "action.close": "Close",
  "action.delete": "Delete ride",
  "action.deleteConfirm": "Delete",
  "action.cancel": "Cancel",
  "action.download": "Download original FIT",
  "action.undo": "Undo",
  "action.retry": "Try again",
  "action.rename": "Rename",
  "action.more": "More",
  "action.zoomBack": "Zoom back",
  "action.resetZoom": "Whole ride",
  "action.showRow": "show",
  "action.resetRow": "Reset",
  "action.open": "Open",

  "lang.label": "Language",
  "theme.label": "Appearance",
  "theme.auto": "Automatic",
  "theme.light": "Light",
  "theme.dark": "Dark",

  "weekday.short.0": "Mon",
  "weekday.short.1": "Tue",
  "weekday.short.2": "Wed",
  "weekday.short.3": "Thu",
  "weekday.short.4": "Fri",
  "weekday.short.5": "Sat",
  "weekday.short.6": "Sun",
  "calendar.week": "Week",
  "calendar.weekShort": "W{n}",
  "calendar.rides": "{n} rides",
  "calendar.ride": "1 ride",
  "calendar.more": "+{n} more",
  "calendar.noRidesWeek": "No rides",
  "calendar.monthTotal": "Month total",
  "calendar.noRidesMonth": "No rides in {month}",
  "calendar.lastRide": "Last ride: {date}",
  "calendar.nextRide": "Next ride: {date}",
  "calendar.longest": "Longest ride",
  "calendar.perRide": "Avg per ride",
  "calendar.vsPrev": "vs. last month",
  "calendar.pickMonth": "Pick a month",
  "calendar.emptyTitle": "No rides yet",
  "calendar.emptyText": "Drop FIT files here or choose them.",
  "calendar.loadError": "The stored rides could not be loaded.",

  "totals.count": "Rides",
  "totals.distance": "Distance",
  "totals.movingTime": "Time",
  "totals.ascent": "Elevation",

  "import.title": "Import {done} / {total}",
  "import.parsing": "reading …",
  "import.added": "imported",
  "import.duplicate": "already there",
  "import.error.not_fit": "not a FIT file",
  "import.error.truncated": "file is cut off",
  "import.error.crc": "checksum is wrong",
  "import.error.not_activity": "not an activity file",
  "import.error.no_records": "no samples",
  "import.error.unknown": "could not be read",
  "import.summary": "{added} new · {duplicates} duplicates · {errors} errors",

  "sport.ride": "Ride",
  "sport.run": "Run",
  "sport.walk": "Walk",
  "sport.hike": "Hike",
  "sport.swim": "Swim",
  "sport.other": "Activity",
  "sport.indoor": "Indoor / virtual",

  "channel.altitude": "Elevation",
  "channel.speed": "Speed",
  "channel.heartRate": "Heart rate",
  "channel.power": "Power",
  "channel.cadence": "Cadence",
  "channel.gradient": "Gradient",
  "channel.temperature": "Temperature",

  "unit.m": "m",
  "unit.km": "km",
  "unit.kmh": "km/h",
  "unit.bpm": "bpm",
  "unit.w": "W",
  "unit.rpm": "rpm",
  "unit.percent": "%",
  "unit.celsius": "°C",
  "unit.kcal": "kcal",

  "stat.distance": "Distance",
  "stat.movingTime": "Moving time",
  "stat.elapsedTime": "Elapsed {v}",
  "stat.ascent": "Ascent",
  "stat.descent": "Descent {v}",
  "stat.speed": "Avg speed",
  "stat.heartRate": "Avg heart rate",
  "stat.power": "Avg power",
  "stat.cadence": "Avg cadence",
  "stat.gradient": "Avg gradient",
  "stat.temperature": "Avg temperature",
  "stat.calories": "Calories",
  "stat.max": "max {v}",
  "stat.whole": "Total {v}",

  "detail.noGps": "No GPS data - this ride has no track.",
  "detail.notFound": "Ride not found.",
  "detail.loadError": "The ride's data could not be loaded.",
  "detail.reparse": "Read again",
  "detail.deleteQuestion": "Delete “{title}”?",
  "detail.deleteText": "The ride is removed from this browser.",
  "detail.deleted": "Ride deleted",
  "detail.axis": "x axis",
  "detail.axisDistance": "Distance",
  "detail.axisTime": "Time",
  "detail.smoothing": "Smoothing",
  "detail.smoothingOff": "Off",
  "detail.channels": "Curves",
  "detail.hint": "Drag: zoom a range · Click: pin a point · Double-click: whole ride",
  "detail.colouring": "Colour",
  "detail.colourPlain": "Plain",
  "detail.follow": "Map follows the point",
  "detail.laps": "Laps",
  "detail.climbs": "Climbs",
  "detail.noClimbs": "No climbs over 20 metres.",
  "detail.col.number": "#",
  "detail.col.start": "Start",
  "detail.col.length": "Length",
  "detail.col.time": "Time",
  "detail.col.speed": "Avg km/h",
  "detail.col.ascent": "Elev.",
  "detail.col.gradient": "Avg %",
  "detail.col.maxGradient": "Max %",
  "detail.col.heartRate": "Avg HR",
  "detail.col.power": "Avg W",
  "detail.device": "Device: {device}",
  "detail.chartsLabel": "Profiles - arrow keys move the point",
  "detail.overview": "Overview of the whole ride",
  "detail.start": "Start",
  "detail.finish": "Finish",

  "action.save": "Save",
  "channel.balance": "L/R balance",
  "detail.noLaps": "This ride has no laps.",
  "detail.noSplits": "Too short for splits.",
  "detail.splitLength": "Split length",
  "detail.splits": "Splits",
  "detail.statistics": "Statistics",
  "group.cadence": "Cadence",
  "group.distanceTime": "Distance and time",
  "group.elevation": "Elevation",
  "group.heart": "Heart rate",
  "group.pedals": "Pedals",
  "group.power": "Power",
  "group.speed": "Speed",
  "group.temperature": "Temperature",
  "group.training": "Training and nutrition",
  "nav.calendar": "Calendar",
  "nav.profile": "Profile",
  "power.curve": "Power curve",
  "power.duration": "Duration",
  "profile.current": "Current values",
  "profile.entryHint": "Only filled fields are saved; empty ones keep their previous value. A ride uses the values that applied on its day - rides before the first entry use the first one.",
  "profile.ftp": "FTP",
  "profile.history": "History",
  "profile.intro": "Your values for zones, intensity (IF), training stress (TSS) and W/kg - only what you enter here, nothing from the FIT files. They stay in this browser.",
  "profile.lthr": "Threshold HR",
  "profile.maxHr": "Max HR",
  "profile.noHistory": "No entries yet.",
  "profile.notSet": "not set",
  "profile.now": "now {v}",
  "profile.removeEntry": "Remove entry",
  "profile.restHr": "Resting HR",
  "profile.saveEntry": "Save entry",
  "profile.title": "Athlete profile",
  "profile.today": "today",
  "profile.validFrom": "Valid from",
  "profile.weight": "Weight",
  "profile.zoneFrom": "from",
  "profile.zonesDefault": "Defaults",
  "profile.zonesOfFtp": "Lower bounds in % of FTP.",
  "profile.zonesOfMaxHr": "Lower bounds in % of max HR.",
  "profile.zonesOrder": "Zones must be ascending.",
  "stat.aerobicTe": "Aerobic training effect",
  "stat.anaerobicTe": "Anaerobic training effect",
  "stat.balance": "L/R balance",
  "stat.best20": "Best 20 min",
  "stat.best20Line": "20 min {v}",
  "stat.descentLabel": "Descent",
  "stat.elapsed": "Elapsed time",
  "stat.ftp": "FTP",
  "stat.heartPctMax": "Avg % of max HR",
  "stat.ifLine": "IF {v}",
  "stat.intensity": "Intensity Factor (IF)",
  "stat.maxAltitude": "Max elevation",
  "stat.maxCadence": "Max cadence",
  "stat.maxGradient": "Max gradient",
  "stat.maxHeartRate": "Max heart rate",
  "stat.maxPower": "Max power",
  "stat.maxSpeed": "Max speed",
  "stat.maxTemperature": "Max temperature",
  "stat.minAltitude": "Min elevation",
  "stat.minTemperature": "Min temperature",
  "stat.movingSpeed": "Avg speed",
  "stat.normalizedPower": "Normalized Power (NP)",
  "stat.npPerKilo": "NP per kg",
  "stat.overallSpeed": "Avg incl. stops",
  "stat.powerPerKilo": "Avg W/kg",
  "stat.smoothLeft": "Pedal smoothness L",
  "stat.smoothRight": "Pedal smoothness R",
  "stat.torqueLeft": "Torque effectiveness L",
  "stat.torqueRight": "Torque effectiveness R",
  "stat.tss": "Training Stress Score (TSS)",
  "stat.work": "Work",
  "totals.stress": "TSS",
  "unit.kg": "kg",
  "unit.percentLeft": "% left",
  "zones.askFtp": "Power zones, IF and TSS need your FTP.",
  "zones.askMaxHr": "Heart-rate zones need your maximum heart rate.",
  "zones.ftpIs": "FTP {v} W",
  "zones.maxHrIs": "Max HR {v} bpm",
  "zones.heart": "Heart-rate zones",
  "zones.power": "Power zones",
  "zones.toProfile": "All values in the profile",
  "zones.h1": "Recovery",
  "zones.h2": "Endurance",
  "zones.h3": "Tempo",
  "zones.h4": "Threshold",
  "zones.h5": "Maximum",
  "zones.p1": "Active recovery",
  "zones.p2": "Endurance",
  "zones.p3": "Tempo",
  "zones.p4": "Threshold",
  "zones.p5": "VO2max",
  "zones.p6": "Anaerobic",
  "zones.p7": "Neuromuscular",
  "tray.drop": "Drop FIT files or folders here",
  "tray.or": "or",
  "tray.chooseFiles": "Choose files",
  "tray.chooseFolder": "Choose a folder",
  "tray.hint": "Several files: select them with Ctrl or Shift in the chooser. A folder brings every .fit file in it, subfolders included.",
  "tray.count": "{n} files · {size}",
  "tray.clear": "Remove all",
  "tray.remove": "Remove",
  "tray.skipped": "{n} files without .fit were left out.",
  "tray.importOne": "Import 1 file",
  "tray.importMany": "Import {n} files",
  "tray.dropHint": "Drop - the files go into the import tray",
  "export.button": "Export FIT",
  "export.title": "Export FIT files as ZIP",
  "export.from": "All rides from",
  "export.count": "{n} rides from this day on",
  "export.countOne": "1 ride from this day on",
  "export.download": "Download ZIP",
  "export.packing": "packing …",
  "export.done": "{n} files exported",
  "export.failed": "The export failed.",
  "power.ofRide": "whole ride",
  "power.ofStretch": "section",
};
